import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Settings as SettingsIcon, 
  CreditCard, 
  Cpu,
  Link,
  Save,
  Plus,
  History,
  Sparkles,
  Key,
  CheckCircle,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  Zap,
  Type,
  Image as ImageIcon
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { configApi, creditsApi } from '../lib/api';
import { useCredits } from '../context/CreditsContext';

const AI_PROVIDERS = [
  { id: 'openai', name: 'OpenAI', models: ['gpt-5.2', 'gpt-4o', 'gpt-4.1'] },
  { id: 'anthropic', name: 'Anthropic', models: ['claude-sonnet-4-5-20250929', 'claude-haiku-4-5-20251001'] },
  { id: 'gemini', name: 'Gemini', models: ['gemini-3-flash-preview', 'gemini-2.5-pro'] },
];

const APIKeyStatus = ({ status, message }) => {
  if (!status) return null;
  
  const statusConfig = {
    valid: { icon: CheckCircle, color: 'text-emerald-500', bg: 'bg-emerald-50', label: 'Válida' },
    invalid: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50', label: 'Inválida' },
    rate_limited: { icon: AlertCircle, color: 'text-amber-500', bg: 'bg-amber-50', label: 'Rate Limited' },
    no_credits: { icon: AlertCircle, color: 'text-orange-500', bg: 'bg-orange-50', label: 'Sin créditos' },
    error: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50', label: 'Error' },
    testing: { icon: Loader2, color: 'text-blue-500', bg: 'bg-blue-50', label: 'Probando...' },
  };
  
  const config = statusConfig[status] || statusConfig.error;
  const Icon = config.icon;
  
  return (
    <div className={`flex items-center gap-2 p-3 rounded-lg ${config.bg} mt-3`}>
      <Icon className={`w-5 h-5 ${config.color} ${status === 'testing' ? 'animate-spin' : ''}`} />
      <div>
        <span className={`font-medium ${config.color}`}>{config.label}</span>
        {message && <p className="text-sm text-slate-600 mt-0.5">{message}</p>}
      </div>
    </div>
  );
};

const AIConfigTab = () => {
  const [config, setConfig] = useState({ provider: 'openai', models: {}, apiKey: '', useEmergentKey: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [keyStatus, setKeyStatus] = useState(null);
  const [keyMessage, setKeyMessage] = useState('');

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await configApi.get();
        setConfig({
          ...response.data.ai,
          apiKey: response.data.ai.apiKey || '',
          useEmergentKey: response.data.ai.useEmergentKey !== false
        });
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  // Reset key status when changing options
  useEffect(() => {
    setKeyStatus(null);
    setKeyMessage('');
  }, [config.useEmergentKey, config.provider]);

  const validateApiKey = (key) => {
    if (!key || key.length < 10) {
      return { valid: false, error: 'La API key debe tener al menos 10 caracteres' };
    }
    
    // Basic format validation per provider
    if (config.provider === 'openai' && !key.startsWith('sk-')) {
      return { valid: false, error: 'Las API keys de OpenAI comienzan con "sk-"' };
    }
    
    if (config.provider === 'anthropic' && !key.startsWith('sk-ant-')) {
      return { valid: false, error: 'Las API keys de Anthropic comienzan con "sk-ant-"' };
    }
    
    return { valid: true };
  };

  const handleTestConnection = async () => {
    // Validate first
    const validation = validateApiKey(config.apiKey);
    if (!validation.valid) {
      setKeyStatus('invalid');
      setKeyMessage(validation.error);
      return;
    }

    setTesting(true);
    setKeyStatus('testing');
    setKeyMessage('Probando conexión con el proveedor...');

    try {
      const response = await configApi.testAIKey({
        provider: config.provider,
        apiKey: config.apiKey,
        model: config.models?.[config.provider]
      });

      if (response.data.success) {
        setKeyStatus('valid');
        setKeyMessage(response.data.message);
        toast.success('Conexión exitosa');
      } else {
        setKeyStatus(response.data.status || 'error');
        setKeyMessage(response.data.error);
      }
    } catch (error) {
      setKeyStatus('error');
      setKeyMessage(error.response?.data?.error || 'Error al probar la conexión');
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    // Validate if using own key
    if (!config.useEmergentKey) {
      const validation = validateApiKey(config.apiKey);
      if (!validation.valid) {
        toast.error(validation.error);
        setKeyStatus('invalid');
        setKeyMessage(validation.error);
        return;
      }
    }

    setSaving(true);
    try {
      await configApi.updateAI({
        provider: config.provider,
        models: config.models,
        apiKey: config.useEmergentKey ? '' : config.apiKey,
        useEmergentKey: config.useEmergentKey
      });
      toast.success('Configuración de IA actualizada');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const selectedProvider = AI_PROVIDERS.find(p => p.id === config.provider);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Cpu className="w-5 h-5" />
            Proveedor de IA
          </CardTitle>
          <CardDescription>
            Selecciona el proveedor y modelo de IA para las mejoras de contenido
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Proveedor activo</Label>
            <Select 
              value={config.provider} 
              onValueChange={(v) => setConfig({ ...config, provider: v })}
            >
              <SelectTrigger className="mt-2" data-testid="select-ai-provider">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AI_PROVIDERS.map((provider) => (
                  <SelectItem key={provider.id} value={provider.id}>
                    {provider.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedProvider && (
            <div>
              <Label>Modelo</Label>
              <Select 
                value={config.models?.[config.provider] || selectedProvider.models[0]}
                onValueChange={(v) => setConfig({
                  ...config,
                  models: { ...config.models, [config.provider]: v }
                })}
              >
                <SelectTrigger className="mt-2" data-testid="select-ai-model">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {selectedProvider.models.map((model) => (
                    <SelectItem key={model} value={model}>
                      {model}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* API Key Configuration */}
          <div className="pt-4 border-t">
            <Label className="text-base font-medium">Configuración de API Key</Label>
            
            <div className="mt-4 space-y-3">
              <div className="flex items-center gap-3">
                <input
                  type="radio"
                  id="use-emergent"
                  name="api-key-choice"
                  checked={config.useEmergentKey}
                  onChange={() => setConfig({ ...config, useEmergentKey: true })}
                  className="w-4 h-4 text-indigo-600"
                />
                <label htmlFor="use-emergent" className="flex-1">
                  <span className="font-medium text-slate-900">Usar Emergent Universal Key</span>
                  <p className="text-sm text-slate-500">Los créditos se deducen de tu balance de Emergent</p>
                </label>
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="radio"
                  id="use-own-key"
                  name="api-key-choice"
                  checked={!config.useEmergentKey}
                  onChange={() => setConfig({ ...config, useEmergentKey: false })}
                  className="w-4 h-4 text-indigo-600 mt-1"
                />
                <label htmlFor="use-own-key" className="flex-1">
                  <span className="font-medium text-slate-900">Usar mi propia API Key</span>
                  <p className="text-sm text-slate-500 mb-2">Introduce tu clave de API del proveedor seleccionado</p>
                  
                  {!config.useEmergentKey && (
                    <div className="space-y-3">
                      <div className="relative">
                        <Input
                          type={showApiKey ? "text" : "password"}
                          value={config.apiKey}
                          onChange={(e) => {
                            setConfig({ ...config, apiKey: e.target.value });
                            setKeyStatus(null);
                            setKeyMessage('');
                          }}
                          placeholder={`Introduce tu API key de ${selectedProvider?.name || 'IA'}`}
                          className={`pr-20 ${keyStatus === 'invalid' || keyStatus === 'error' ? 'border-red-300 focus:ring-red-500' : ''} ${keyStatus === 'valid' ? 'border-emerald-300 focus:ring-emerald-500' : ''}`}
                          data-testid="api-key-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowApiKey(!showApiKey)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500 hover:text-slate-700"
                        >
                          {showApiKey ? 'Ocultar' : 'Mostrar'}
                        </button>
                      </div>
                      
                      {/* Test Connection Button */}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleTestConnection}
                        disabled={testing || !config.apiKey}
                        data-testid="test-connection-btn"
                      >
                        {testing ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Zap className="w-4 h-4 mr-2" />
                        )}
                        {testing ? 'Probando...' : 'Probar conexión'}
                      </Button>
                      
                      {/* Status Indicator */}
                      <APIKeyStatus status={keyStatus} message={keyMessage} />
                    </div>
                  )}
                </label>
              </div>
            </div>
          </div>

          <div className="pt-4">
            <Button onClick={handleSave} disabled={saving} data-testid="save-ai-config">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Guardando...' : 'Guardar configuración'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {config.useEmergentKey && (
        <Card className="bg-indigo-50 border-indigo-100">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-900">Usando Emergent Universal Key</h4>
                <p className="text-sm text-slate-600 mt-1">
                  Estás usando la clave universal de Emergent que funciona con OpenAI, Anthropic y Gemini.
                  Los créditos se deducen de tu balance.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {!config.useEmergentKey && config.apiKey && (
        <Card className="bg-emerald-50 border-emerald-100">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Key className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-900">Usando tu propia API Key</h4>
                <p className="text-sm text-slate-600 mt-1">
                  Las llamadas a la IA se facturarán directamente a tu cuenta de {selectedProvider?.name}.
                  No se consumirán créditos de Emergent.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const CreditsTab = () => {
  const { credits, refreshCredits } = useCredits();
  const [addAmount, setAddAmount] = useState('');
  const [adding, setAdding] = useState(false);

  const handleAddCredits = async () => {
    const amount = parseInt(addAmount);
    if (!amount || amount <= 0) {
      toast.error('Introduce una cantidad válida');
      return;
    }

    setAdding(true);
    try {
      await creditsApi.add({ amount, description: 'Recarga manual' });
      toast.success(`${amount} créditos añadidos`);
      setAddAmount('');
      refreshCredits();
    } catch (error) {
      toast.error('Error al añadir créditos');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Balance Card */}
      <Card className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-indigo-100 text-sm">Balance actual</p>
              <p className="text-5xl font-bold mt-2" data-testid="credits-balance-display">{credits.balance}</p>
              <p className="text-indigo-100 text-sm mt-1">créditos disponibles</p>
            </div>
            <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center">
              <CreditCard className="w-10 h-10" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Credits */}
      <Card>
        <CardHeader>
          <CardTitle>Añadir créditos</CardTitle>
          <CardDescription>Recarga tu balance de créditos</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Input
              type="number"
              value={addAmount}
              onChange={(e) => setAddAmount(e.target.value)}
              placeholder="Cantidad"
              className="w-40"
              data-testid="add-credits-input"
            />
            <Button onClick={handleAddCredits} disabled={adding} data-testid="add-credits-btn">
              <Plus className="w-4 h-4 mr-2" />
              {adding ? 'Añadiendo...' : 'Añadir'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Costs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Costos por operación</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Operación</TableHead>
                <TableHead className="text-right">Créditos</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(credits.costs || {}).map(([operation, cost]) => (
                <TableRow key={operation}>
                  <TableCell className="font-medium">
                    {operation.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant="outline">{cost}</Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Historial de uso
          </CardTitle>
        </CardHeader>
        <CardContent>
          {credits.history?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Operación</TableHead>
                  <TableHead>Descripción</TableHead>
                  <TableHead className="text-right">Créditos</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {credits.history.slice(0, 10).map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="font-medium">
                      {entry.operation.replace(/_/g, ' ')}
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm">
                      {entry.description}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={entry.cost > 0 ? 'text-red-500' : 'text-emerald-500'}>
                        {entry.cost > 0 ? `-${entry.cost}` : `+${Math.abs(entry.cost)}`}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {entry.balance_after}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-center text-slate-500 py-8">No hay historial de uso</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const IntegrationsTab = () => {
  const [mauticConfig, setMauticConfig] = useState({ baseUrl: '', clientId: '', clientSecret: '' });
  const [n8nConfig, setN8NConfig] = useState({ 
    textWebhook: '', 
    imageWebhook: '',
    designWebhook: '', 
    automationWebhook: '' 
  });
  const [savingMautic, setSavingMautic] = useState(false);
  const [savingN8N, setSavingN8N] = useState(false);
  const [mauticConfigured, setMauticConfigured] = useState(false);
  const [testingMautic, setTestingMautic] = useState(false);
  const [mauticTestResult, setMauticTestResult] = useState(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await configApi.get();
        const data = response.data;
        
        // Load Mautic config - show existing values
        if (data.mautic) {
          setMauticConfig({
            baseUrl: data.mautic.baseUrl || '',
            clientId: data.mautic.clientId || '',
            clientSecret: '' // Don't show password, user must re-enter if changing
          });
          setMauticConfigured(data.mautic.configured || false);
        }
        
        // Load n8n config
        if (data.n8n) {
          setN8NConfig({
            textWebhook: data.n8n.textWebhook || data.n8n.webhookUrl || '',
            imageWebhook: data.n8n.imageWebhook || '',
            designWebhook: data.n8n.designWebhook || '',
            automationWebhook: data.n8n.automationWebhook || ''
          });
        }
      } catch (error) {
        console.error('Error loading config:', error);
      }
    };
    fetchConfig();
  }, []);

  const handleSaveMautic = async () => {
    if (!mauticConfig.baseUrl) {
      toast.error('La URL de Mautic es requerida');
      return;
    }
    
    setSavingMautic(true);
    setMauticTestResult(null);
    try {
      await configApi.updateMautic(mauticConfig);
      toast.success('Configuración de Mautic guardada correctamente');
      setMauticConfigured(true);
    } catch (error) {
      toast.error('Error al guardar la configuración');
    } finally {
      setSavingMautic(false);
    }
  };

  const handleTestMautic = async () => {
    setTestingMautic(true);
    setMauticTestResult(null);
    try {
      const response = await configApi.testMautic();
      setMauticTestResult(response.data);
      if (response.data.success) {
        toast.success('¡Conexión con Mautic exitosa!');
      } else {
        toast.error(response.data.error || 'Error de conexión');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Error al probar la conexión';
      setMauticTestResult({ success: false, error: errorMsg });
      toast.error(errorMsg);
    } finally {
      setTestingMautic(false);
    }
  };

  const handleSaveN8N = async () => {
    setSavingN8N(true);
    try {
      await configApi.updateN8N(n8nConfig);
      toast.success('Configuración de n8n guardada correctamente');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSavingN8N(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Mautic */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Mautic</CardTitle>
              <CardDescription>Conecta tu instancia de Mautic para gestionar contactos y enviar emails</CardDescription>
            </div>
            {mauticConfigured && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-testid="mautic-configured-badge">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Configurado
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="mauticUrl">URL de Mautic</Label>
            <Input
              id="mauticUrl"
              value={mauticConfig.baseUrl}
              onChange={(e) => setMauticConfig({ ...mauticConfig, baseUrl: e.target.value })}
              placeholder="https://tu-mautic.com"
              className="mt-2"
              data-testid="mautic-url-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="clientId">Client ID</Label>
              <Input
                id="clientId"
                value={mauticConfig.clientId}
                onChange={(e) => setMauticConfig({ ...mauticConfig, clientId: e.target.value })}
                placeholder="Tu Client ID"
                className="mt-2"
                data-testid="mautic-client-id"
              />
            </div>
            <div>
              <Label htmlFor="clientSecret">Client Secret</Label>
              <Input
                id="clientSecret"
                type="password"
                value={mauticConfig.clientSecret}
                onChange={(e) => setMauticConfig({ ...mauticConfig, clientSecret: e.target.value })}
                placeholder={mauticConfigured ? "••••••••  (dejar vacío para mantener)" : "Tu Client Secret"}
                className="mt-2"
                data-testid="mautic-client-secret"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <Button onClick={handleSaveMautic} disabled={savingMautic} data-testid="save-mautic-btn">
              <Save className="w-4 h-4 mr-2" />
              {savingMautic ? 'Guardando...' : 'Guardar configuración'}
            </Button>
            <Button 
              variant="outline" 
              onClick={handleTestMautic} 
              disabled={testingMautic || !mauticConfigured}
              data-testid="test-mautic-btn"
            >
              {testingMautic ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Zap className="w-4 h-4 mr-2" />
              )}
              {testingMautic ? 'Probando...' : 'Probar conexión'}
            </Button>
          </div>
          {mauticTestResult && (
            <div className={`p-3 rounded-lg text-sm ${mauticTestResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              {mauticTestResult.success ? (
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  {mauticTestResult.message || 'Conexión exitosa con Mautic'}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <XCircle className="w-4 h-4" />
                  {mauticTestResult.error || 'Error de conexión'}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* n8n Workflows */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-orange-500" />
            n8n Workflows
          </CardTitle>
          <CardDescription>Configura las URLs de los webhooks de n8n para las diferentes automatizaciones</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Workflow 1: Texto */}
          <div className="p-4 border rounded-lg bg-slate-50">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                <Type className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <h4 className="font-medium text-slate-900">Generación de Textos</h4>
                <p className="text-xs text-slate-500">Genera contenido de texto con IA para tus campañas</p>
              </div>
            </div>
            <div>
              <Label htmlFor="textWebhook">URL del Webhook</Label>
              <Input
                id="textWebhook"
                value={n8nConfig.textWebhook}
                onChange={(e) => setN8NConfig({ ...n8nConfig, textWebhook: e.target.value })}
                placeholder="https://tu-n8n.com/webhook/marketflow-texto"
                className="mt-2"
                data-testid="n8n-text-webhook"
              />
            </div>
          </div>

          {/* Workflow 2: Imágenes */}
          <div className="p-4 border rounded-lg bg-slate-50">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-pink-100 flex items-center justify-center">
                <ImageIcon className="w-4 h-4 text-pink-600" />
              </div>
              <div>
                <h4 className="font-medium text-slate-900">Generación de Imágenes</h4>
                <p className="text-xs text-slate-500">Genera imágenes con IA para tus emails</p>
              </div>
            </div>
            <div>
              <Label htmlFor="imageWebhook">URL del Webhook</Label>
              <Input
                id="imageWebhook"
                value={n8nConfig.imageWebhook}
                onChange={(e) => setN8NConfig({ ...n8nConfig, imageWebhook: e.target.value })}
                placeholder="https://tu-n8n.com/webhook/marketflow-imagen"
                className="mt-2"
                data-testid="n8n-image-webhook"
              />
            </div>
          </div>

          {/* Workflow 3: Diseño */}
          <div className="p-4 border rounded-lg bg-slate-50">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <h4 className="font-medium text-slate-900">Generación de Diseño</h4>
                <p className="text-xs text-slate-500">Genera paletas de colores y estilos para tus emails</p>
              </div>
            </div>
            <div>
              <Label htmlFor="designWebhook">URL del Webhook</Label>
              <Input
                id="designWebhook"
                value={n8nConfig.designWebhook}
                onChange={(e) => setN8NConfig({ ...n8nConfig, designWebhook: e.target.value })}
                placeholder="https://tu-n8n.com/webhook/marketflow-completo"
                className="mt-2"
                data-testid="n8n-design-webhook"
              />
            </div>
          </div>

          {/* Workflow 4: Automatizaciones */}
          <div className="p-4 border rounded-lg bg-slate-50">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                <Zap className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <h4 className="font-medium text-slate-900">Automatizaciones</h4>
                <p className="text-xs text-slate-500">Notificaciones y reacciones a eventos de Mautic</p>
              </div>
            </div>
            <div>
              <Label htmlFor="automationWebhook">URL del Webhook</Label>
              <Input
                id="automationWebhook"
                value={n8nConfig.automationWebhook}
                onChange={(e) => setN8NConfig({ ...n8nConfig, automationWebhook: e.target.value })}
                placeholder="https://tu-n8n.com/webhook/marketflow-automation"
                className="mt-2"
                data-testid="n8n-automation-webhook"
              />
            </div>
          </div>

          <Button onClick={handleSaveN8N} disabled={savingN8N} data-testid="save-n8n-btn">
            <Save className="w-4 h-4 mr-2" />
            {savingN8N ? 'Guardando...' : 'Guardar configuración de n8n'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export const Settings = () => {
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'ai';

  return (
    <div className="p-8 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold font-[Manrope] text-slate-900">Configuración</h1>
        <p className="text-slate-500 mt-1">Gestiona las configuraciones del sistema</p>
      </div>

      <Tabs defaultValue={defaultTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="ai" className="gap-2" data-testid="tab-ai">
            <Cpu className="w-4 h-4" />
            IA
          </TabsTrigger>
          <TabsTrigger value="credits" className="gap-2" data-testid="tab-credits">
            <CreditCard className="w-4 h-4" />
            Créditos
          </TabsTrigger>
          <TabsTrigger value="integrations" className="gap-2" data-testid="tab-integrations">
            <Link className="w-4 h-4" />
            Integraciones
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ai">
          <AIConfigTab />
        </TabsContent>

        <TabsContent value="credits">
          <CreditsTab />
        </TabsContent>

        <TabsContent value="integrations">
          <IntegrationsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
